import { navBarActionButtonStyles } from "layout/nav-bar/nav-bar.styles";
import { stringifyUrl } from "query-string";
import { useCallback } from "react";

import { AccountInfo } from "@azure/msal-browser";
import { useAccount } from "@azure/msal-react";
import { useBlocksClinicalStoreActions } from "@bps/blocks-premier";
import {
  ActionButton,
  IIconProps,
  Persona,
  PersonaSize,
  useTheme
} from "@bps/fluent-ui";
import { CloudApplications, logout } from "@bps/msal";

import { usePermissionsChecker } from "../../core/auth/Permissions";
import { Permission } from "../../core/common-types/permission.enum";
import { CloudApplicationDto } from "../../core/gateways/bp-id/bp-id.dtos";
import { useRefCloudApplications } from "../../core/gateways/bp-id/bp-id.hooks";
import { useCurrentUserQuery } from "../../core/gateways/user/UserHooks";

interface IUserProfileMenuItem {
  key: string;
  iconProps: IIconProps;
  text: string;
  onClick: () => void;
}

enum UserProfileMenuItems {
  Logout = "logout",
  BpId = "bp-id"
}

export const UserProfile = () => {
  const theme = useTheme();
  const { resetSessionValues, resetLocks } = useBlocksClinicalStoreActions();

  const { data: bpIdUrl } = useRefCloudApplications<string | undefined>({
    select: useCallback((cloudApplications: CloudApplicationDto[]) => {
      return cloudApplications.find(a => a.code === CloudApplications.BpId)
        ?.primaryUrl;
    }, [])
  });

  const { data: user } = useCurrentUserQuery();

  const hasPermissions = usePermissionsChecker();
  const hasBpmWebAllowedPermission = hasPermissions(
    Permission.ProsBpmWebAllowed
  );

  const userInitials = `${user?.name.firstName[0] ?? ""}${
    user?.name.lastName[0] ?? ""
  }`.toUpperCase();

  const account = useAccount();
  const claims = account?.idTokenClaims as AccountInfo["idTokenClaims"] & {
    given_name?: string;
    family_name?: string;
    email: string;
    bpu: string;
    bpt: string;
  };

  const handleLogout = () => {
    hasBpmWebAllowedPermission && resetSessionValues();
    resetLocks();
    logout().then(() => {});
  };

  const items: IUserProfileMenuItem[] = [
    {
      key: UserProfileMenuItems.BpId,
      iconProps: {
        iconName: "Share",
        styles: { root: { order: 1 } }
      },
      text: "My Best Practice ID",
      onClick: () => {
        if (!bpIdUrl || !account) return;

        const url = stringifyUrl({
          url: bpIdUrl,
          query: {
            // eslint-disable-next-line camelcase
            login_hint: btoa(
              !!account.username ? account.username : claims.email
            )
          }
        });

        // ⚠️ Used noopener to not convey msal sessionStorage artifacts
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/open#noopener
        window.open(url, "_blank", "noopener=true");
      }
    },
    {
      key: "logout",
      text: "Sign out",
      iconProps: {
        iconName: "",
        styles: { root: { order: 1 } }
      },
      onClick: handleLogout
    }
  ].filter(item => {
    if (item.key === UserProfileMenuItems.BpId && !bpIdUrl) return false;

    return true;
  });

  return (
    <ActionButton
      data-test="Persona"
      menuProps={{
        shouldFocusOnMount: true,
        items
      }}
      styles={navBarActionButtonStyles(theme)}
    >
      <Persona
        styles={({ theme }) => ({
          root: { maxWidth: "24px", marginRight: "4px" },
          primaryText: {
            color: theme.palette.white,
            selectors: {
              "&:hover": {
                color: theme.palette.white
              }
            }
          }
        })}
        text=""
        imageInitials={userInitials}
        size={PersonaSize.size24}
      />
    </ActionButton>
  );
};
